import { useEffect, useRef, useState } from 'react';
import { OrderTicket } from '../../trading/OrderTicket';
import { TickChart } from '../../chart/TickChart';
import { oppositesOf } from '../../trading/bulkActions';
import { levelPrice, levelText, pointSize, type LevelKind } from '../../trading/levels';
import type { Side } from '../../trading/tradeRules';
import { useMoneyPerLot } from '../../trading/useMoneyPerLot';
import { useTradeClient } from '../../trading/useTrading';
import { useSymbols } from '../../market/useSymbols';
import { useStream } from '../../stream/stream';
import type { Position } from '../../stream/accountModel';
import { useWorkspace } from '../../workspace/workspaceStore';
import { type OrderWindowContext } from './orderWindowStore';

export type { OrderWindowContext };

type Tab = 'order' | 'modify' | 'closeBy';

const navBtn =
  'flex items-center gap-2 px-3 py-1.5 text-left hover:bg-panel2 disabled:cursor-not-allowed disabled:opacity-40';
const winBtn = 'grid h-5 w-5 place-items-center rounded text-sm leading-none hover:bg-black/10';

const kindOf = (type: string) => type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

/**
 * The full MT5-style "Order" window: a title bar, a left-hand view list, the order ticket (covering market and pending
 * kinds through its own Type dropdown, plus a Close button describing the position when opened for one), Modify Position
 * (stop loss and take profit, each as a price and as points, like MT5's own dialog), or Close By, and a live tick chart.
 */
export function ClassicOrderWindow({
  open,
  context,
  allPositions,
  digitsOf,
  onClose,
  onCloseBy,
}: {
  open: boolean;
  context: OrderWindowContext | null;
  allPositions: Position[];
  digitsOf: (symbol: string) => number;
  onClose: () => void;
  onCloseBy: (other: Position) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [tab, setTab] = useState<Tab>('order');
  // Every opening starts on the Order view (set while rendering, so the old view is never shown first).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setTab('order');
  }

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const activeSymbol = useWorkspace((s) => s.activeSymbol);
  const row = context && context.kind !== 'new' ? context.row : undefined;
  const digits = row ? digitsOf(row.symbol) : activeSymbol ? digitsOf(activeSymbol) : 5;
  const opposites = context?.kind === 'position' ? oppositesOf(context.row, allPositions) : [];

  const title = !context
    ? ''
    : context.kind === 'position'
      ? `Position: #${context.row.ticket} ${context.row.side.toLowerCase()} ${context.row.volume} ${context.row.symbol} ${context.row.priceOpen.toFixed(digits)}`
      : context.kind === 'order'
        ? `Order: #${context.row.ticket} ${kindOf(context.row.type)} ${context.row.volumeCurrent} ${context.row.symbol} ${context.row.priceOrder.toFixed(digits)}`
        : 'Order: New';

  return (
    <dialog
      ref={ref}
      aria-label={title || 'Order window'}
      onClose={onClose}
      onCancel={onClose}
      className="m-auto w-[calc(100%-2rem)] max-w-6xl overflow-hidden rounded-[var(--radius)] border border-line bg-panel p-0 text-fg shadow-2xl backdrop:bg-black/60"
    >
      {open && context && (
        <>
          <div className="flex items-center justify-between border-b border-line bg-panel2 px-3 py-1.5 text-sm">
            <span className="truncate font-semibold">{title}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={winBtn}
                aria-label="Minimize"
                title="Minimize"
                onClick={onClose}
              >
                —
              </button>
              <button type="button" className={winBtn} aria-label="Maximize" title="Maximize" disabled>
                ▢
              </button>
              <button
                type="button"
                className={`${winBtn} hover:bg-down hover:text-black`}
                aria-label="Close"
                title="Close (Esc)"
                onClick={onClose}
              >
                ×
              </button>
            </div>
          </div>
          <div className="flex h-[75vh] max-h-[640px]">
            <nav
              className="flex w-40 shrink-0 flex-col border-r border-line bg-panel2 py-1 text-xs"
              aria-label="Order window views"
            >
              <button
                type="button"
                className={navBtn}
                aria-pressed={tab === 'order'}
                onClick={() => setTab('order')}
              >
                Market Execution
              </button>
              {context.kind === 'position' && (
                <>
                  <button
                    type="button"
                    className={navBtn}
                    aria-pressed={tab === 'modify'}
                    onClick={() => setTab('modify')}
                  >
                    Modify Position
                  </button>
                  <button
                    type="button"
                    className={navBtn}
                    disabled={opposites.length === 0}
                    aria-pressed={tab === 'closeBy'}
                    onClick={() => setTab('closeBy')}
                  >
                    Close By
                  </button>
                </>
              )}
              <div className="mt-auto border-t border-line pt-1">
                <button type="button" className={navBtn} disabled title="Not available in the web terminal">
                  One Click Trading
                </button>
                <button type="button" className={navBtn} disabled title="Shown on the right already">
                  Tick Chart
                </button>
                <button type="button" className={navBtn} disabled title="Not available in the web terminal">
                  Depth of Market
                </button>
                <button type="button" className={navBtn} onClick={onClose}>
                  Minimize
                </button>
              </div>
            </nav>
            <div className="min-w-0 flex-1 overflow-auto">
              {tab === 'order' && (
                <OrderTicket
                  onSuccess={onClose}
                  closeContext={
                    context.kind === 'position'
                      ? {
                          ticket: context.row.ticket,
                          side: context.row.side as Side,
                          volume: context.row.volume,
                          symbol: context.row.symbol,
                          priceOpen: context.row.priceOpen,
                        }
                      : undefined
                  }
                />
              )}
              {tab === 'modify' && context.kind === 'position' && (
                <ModifyPositionTab position={context.row} digits={digits} onSuccess={onClose} />
              )}
              {tab === 'closeBy' && context.kind === 'position' && (
                <div className="flex flex-col gap-2 p-3 text-sm">
                  <p className="text-muted">Close #{context.row.ticket} against an opposite position:</p>
                  {opposites.map((o) => (
                    <button
                      key={o.ticket}
                      type="button"
                      className="rounded border border-line px-3 py-1.5 text-left hover:border-accent"
                      onClick={() => onCloseBy(o)}
                    >
                      #{o.ticket} · {o.side.toLowerCase()} {o.volume} {o.symbol}
                    </button>
                  ))}
                  {opposites.length === 0 && (
                    <p className="text-muted">No opposite position to close against.</p>
                  )}
                </div>
              )}
            </div>
            <div className="hidden w-96 shrink-0 border-l border-line lg:block">
              <TickChart />
            </div>
          </div>
        </>
      )}
    </dialog>
  );
}

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent num';
const miniField =
  'w-20 rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent num';

/**
 * MT5's own Modify Position view: stop loss and take profit, each as a price and as a distance in points (editing either
 * updates the other), the live bid/ask in large type, and a status strip with the stops-level hint.
 */
function ModifyPositionTab({
  position,
  digits,
  onSuccess,
}: {
  position: Position;
  digits: number;
  onSuccess: () => void;
}) {
  const client = useTradeClient();
  const info = useSymbols().data?.find((s) => s.name === position.symbol);
  const moneyPerLot = useMoneyPerLot(position.symbol, digits);
  const quote = useStream((s) => s.quotes[position.symbol]);
  const side = position.side as Side;
  const ctx = {
    side,
    entry: position.priceOpen,
    digits,
    moneyPerPrice: moneyPerLot === null ? null : moneyPerLot * position.volume,
    balance: 0,
  };
  const [sl, setSl] = useState(position.stopLoss > 0 ? position.stopLoss.toFixed(digits) : '');
  const [tp, setTp] = useState(position.takeProfit > 0 ? position.takeProfit.toFixed(digits) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pointsOf = (kind: LevelKind, priceText: string): string => {
    const price = Number(priceText);
    if (!(price > 0)) return '';
    return levelText('points', price, kind, ctx) ?? '';
  };
  const setFromPoints = (kind: LevelKind, text: string, set: (v: string) => void) => {
    if (text.trim() === '') return set('');
    const price = levelPrice('points', text, kind, ctx);
    if (price !== null && price > 0) set(price.toFixed(digits));
  };

  const save = async () => {
    if (!client) return;
    const slValue = sl.trim() === '' ? 0 : Number(sl);
    const tpValue = tp.trim() === '' ? 0 : Number(tp);
    if ((sl.trim() !== '' && !(slValue > 0)) || (tp.trim() !== '' && !(tpValue > 0))) {
      return setError('Enter the stop loss and take profit as prices, or leave them empty.');
    }
    setBusy(true);
    setError(null);
    const outcome = await client.modifyPosition(position.ticket, slValue, tpValue, position.trailingPoints);
    setBusy(false);
    if (outcome.ok) onSuccess();
    else setError(outcome.message);
  };

  return (
    <form
      className="flex h-full flex-col gap-4 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <label className="w-24 shrink-0 text-sm text-muted" htmlFor="modify-sl">
            Stop Loss:
          </label>
          <input
            id="modify-sl"
            type="number"
            step={String(pointSize(digits))}
            className={field}
            placeholder="0.00000"
            value={sl}
            onChange={(e) => setSl(e.target.value)}
          />
          <input
            aria-label="Stop loss, in points"
            inputMode="numeric"
            className={miniField}
            placeholder="0"
            value={pointsOf('stopLoss', sl)}
            onChange={(e) => setFromPoints('stopLoss', e.target.value, setSl)}
          />
          <span className="shrink-0 text-xs text-muted">points</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="w-24 shrink-0 text-sm text-muted" htmlFor="modify-tp">
            Take Profit:
          </label>
          <input
            id="modify-tp"
            type="number"
            step={String(pointSize(digits))}
            className={field}
            placeholder="0.00000"
            value={tp}
            onChange={(e) => setTp(e.target.value)}
          />
          <input
            aria-label="Take profit, in points"
            inputMode="numeric"
            className={miniField}
            placeholder="0"
            value={pointsOf('takeProfit', tp)}
            onChange={(e) => setFromPoints('takeProfit', e.target.value, setTp)}
          />
          <span className="shrink-0 text-xs text-muted">points</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-2">
        <p className="num text-4xl font-semibold">
          {quote ? `${quote.bid.toFixed(digits)} / ${quote.ask.toFixed(digits)}` : '— / —'}
        </p>
      </div>

      <p role="alert" className="min-h-[1.25rem] text-sm text-down">
        {error}
      </p>

      <div className="rounded-md border border-line bg-panel2 px-3 py-2 text-xs">
        <p className="font-semibold">
          Modify #{position.ticket} {position.side.toLowerCase()} {position.volume} {position.symbol}{' '}
          {position.priceOpen.toFixed(digits)}
        </p>
        <p className="mt-1 text-muted">
          Stop Loss or Take Profit you set must differ from market price by at least {info?.stopsLevel ?? 0}{' '}
          points. Stops processing is performed on the broker side.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-accent-fg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Modify
        </button>
      </div>
    </form>
  );
}
